/* eslint-disable react/require-default-props */
import React, {forwardRef, useMemo, useState} from 'react';
import {Platform, Pressable, StyleSheet, ViewStyle} from 'react-native';
import {ExternalKeyboardView} from 'react-native-external-keyboard';

import {black} from '/theme/color/on.color';

interface KeyboardFocusableProps {
  children: React.ReactNode;
  onFocus?: () => void;
  onBlur?: () => void;
  onPress?: () => void;
  style?: ViewStyle | ViewStyle[];
  disableFocusRing?: boolean;
  focusRingStyle?: ViewStyle;
  disabled?: boolean;
  focusable?: boolean;
  IsBorder?: boolean;
  viewRef?: any;
  [key: string]: any;
}

const KeyboardFocusable = forwardRef<any, KeyboardFocusableProps>(
  (
    {
      children,
      onFocus = () => {},
      onBlur = () => {},
      onPress = () => {},
      IsBorder = true,
      style,
      disableFocusRing = false,
      focusRingStyle: customFocusRingStyle,
      disabled = false,
      focusable,
      viewRef,
      ...props
    },
    ref,
  ) => {
    const [isTrackingFocused, setIsTrackingFocused] = useState(false);

    const isWeb = Platform.OS === 'web';
    const isFocusable = focusable ?? !disabled;

    const focusRingStyle = customFocusRingStyle || styles.focusRing;

    const FocusableComponent = useMemo(() => {
      if (isWeb) {
        return Pressable;
      }

      const {withKeyboardFocus} = require('react-native-external-keyboard');

      return withKeyboardFocus(Pressable);
    }, [isWeb]);

    const combinedStyle = [
      style,
      !disableFocusRing &&
        isTrackingFocused &&
        (isWeb ? styles.webFocusRing : focusRingStyle),
      isWeb && styles.webCursor,
      isWeb && styles.webReset,
    ];
    const content = (
      <FocusableComponent
        ref={ref}
        viewRef={!isWeb ? viewRef : undefined}
        {...props}
        {...(!isWeb && {tintType: 'none'})}
        style={combinedStyle}
        accessible
        focusable={isFocusable}
        disabled={disabled}
        onFocus={() => {
          if (IsBorder) {
            setIsTrackingFocused(true);
          }

          onFocus();
        }}
        onBlur={() => {
          if (IsBorder) {
            setIsTrackingFocused(false);
          }

          onBlur();
        }}
        onPress={() => {
          onPress();
        }}>
        {children}
      </FocusableComponent>
    );

    if (isWeb) {
      return content;
    }

    return (
      <ExternalKeyboardView
        focusable={Platform.OS === 'android'}
        pointerEvents="box-none">
        {content}
      </ExternalKeyboardView>
    );
  },
);

KeyboardFocusable.displayName = 'KeyboardFocusable';

export default KeyboardFocusable;

const styles = StyleSheet.create({
  focusRing: {
    borderWidth: 3,
    borderColor: black,
    borderRadius: 4,
  },

  webFocusRing: {
    // @ts-ignore
    outlineWidth: 3,
    // @ts-ignore
    outlineColor: black,
    // @ts-ignore
    outlineStyle: 'solid',
    // @ts-ignore
    outlineOffset: 2,
  },

  webReset: {
    outlineWidth: 0,
  },

  webCursor: {
    // @ts-ignore
    cursor: 'pointer',
  },
});
